import type { TileType } from '@vagusx/clients';
import type { TileDefinition, TileRenderContext } from './tiles/registry/types';
import {
  openTasksTile,
  completedTasksTile,
  completionRateTile,
  referralsCountTile,
  activeUsersTile,
} from './tiles/registry/stat-tiles';
import { tasksByLabelTile } from './tiles/registry/tasks-by-label';
import { tasksByAssigneeTile } from './tiles/registry/tasks-by-assignee';
import { openTasksByAgeTile } from './tiles/registry/open-tasks-by-age';
import { throughputTrendTile } from './tiles/registry/throughput-trend';
import { timeToResolutionTile } from './tiles/registry/time-to-resolution';
import {
  outboundCallsTile,
  outboundScheduledTile,
  outboundVoicemailsTile,
  outboundNotAnsweredTile,
  outboundReachedTile,
  outboundBookingRateTile,
  outboundPendingTile,
  outboundInProgressTile,
} from './tiles/registry/outbound-stats';
import { outboundEfficiencyTile } from './tiles/registry/outbound-efficiency';
import { outboundOutcomesTile } from './tiles/registry/outbound-outcomes';
import { outboundTrendTile } from './tiles/registry/outbound-trend';

/**
 * Re-export per-tile contract so existing imports
 * (`@/components/dashboard/tile-registry`) keep working without
 * code-spread updates.
 */
export type { TileDefinition, TileRenderContext };

/**
 * Aggregator for every tile module. Each tile module owns its own
 * render fn, drilldown pattern, config schema, and (for chart tiles)
 * its lazy-load boundary. This file does no logic — it just collects.
 *
 * Adding a new tile is two changes:
 *   1. Create `tiles/registry/{tile-name}.tsx`
 *   2. Append the import + entry below
 *
 * Per-tile config schemas live next to their definitions; the dashboard
 * route uses `TILE_REGISTRY[type].configSchema.parse(tile.config)` to
 * validate persisted layouts on load.
 */
export const TILE_REGISTRY: Record<TileType, TileDefinition> = {
  open_tasks: openTasksTile,
  completed_tasks: completedTasksTile,
  completion_rate: completionRateTile,
  tasks_by_label: tasksByLabelTile,
  tasks_by_assignee: tasksByAssigneeTile,
  open_tasks_by_age: openTasksByAgeTile,
  referrals_count: referralsCountTile,
  active_users_count: activeUsersTile,
  throughput_trend: throughputTrendTile,
  time_to_resolution_by_label: timeToResolutionTile,
  outbound_calls: outboundCallsTile,
  outbound_scheduled: outboundScheduledTile,
  outbound_voicemails: outboundVoicemailsTile,
  outbound_not_answered: outboundNotAnsweredTile,
  outbound_reached: outboundReachedTile,
  outbound_booking_rate: outboundBookingRateTile,
  outbound_outcomes: outboundOutcomesTile,
  outbound_call_trend: outboundTrendTile,
  outbound_efficiency: outboundEfficiencyTile,
  outbound_pending: outboundPendingTile,
  outbound_in_progress: outboundInProgressTile,
};

/** All tile definitions, ordered for consistent display in the catalog. */
export const TILE_DEFINITIONS: TileDefinition[] = [
  TILE_REGISTRY.open_tasks,
  TILE_REGISTRY.completion_rate,
  TILE_REGISTRY.completed_tasks,
  TILE_REGISTRY.referrals_count,
  TILE_REGISTRY.throughput_trend,
  TILE_REGISTRY.time_to_resolution_by_label,
  TILE_REGISTRY.tasks_by_label,
  TILE_REGISTRY.tasks_by_assignee,
  TILE_REGISTRY.open_tasks_by_age,
  TILE_REGISTRY.active_users_count,
  // Outbound call metrics.
  TILE_REGISTRY.outbound_calls,
  TILE_REGISTRY.outbound_scheduled,
  TILE_REGISTRY.outbound_voicemails,
  TILE_REGISTRY.outbound_not_answered,
  TILE_REGISTRY.outbound_reached,
  TILE_REGISTRY.outbound_booking_rate,
  TILE_REGISTRY.outbound_call_trend,
  TILE_REGISTRY.outbound_outcomes,
  TILE_REGISTRY.outbound_efficiency,
  TILE_REGISTRY.outbound_pending,
  TILE_REGISTRY.outbound_in_progress,
];

export function isKnownTileType(type: string): type is TileType {
  return type in TILE_REGISTRY;
}

/**
 * Validate a persisted tile's `config` blob against its tile-type
 * configSchema. Bad config (older client wrote a shape newer tile no
 * longer accepts, or a tampered preferences row) falls back to the
 * tile's `defaultConfig` rather than rendering broken — keeps
 * dashboards self-healing across schema evolutions.
 */
export function validateTileConfig(
  type: TileType,
  config: unknown
): Record<string, unknown> | undefined {
  const def = TILE_REGISTRY[type];
  const result = def.configSchema.safeParse(config ?? def.defaultConfig);
  return result.success
    ? (result.data as Record<string, unknown>)
    : (def.defaultConfig as Record<string, unknown>);
}

/**
 * Position template for the default layout. IDs are generated per
 * call to `defaultDashboardLayout()` so two users hitting "Reset to
 * default" don't end up with identical tile UUIDs in their saved
 * preferences.
 *
 * Note: `completion_rate` was dropped per customer feedback
 * ("doesn't tell me much"); the tile is still in the catalog if a
 * user wants to add it back.
 */
const DEFAULT_LAYOUT_POSITIONS = Object.freeze<
  ReadonlyArray<{ type: TileType; x: number; y: number; w: number; h: number }>
>([
  // Row 0 — Scheduling (outbound call) numbers, 4 across.
  { type: 'outbound_calls', x: 0, y: 0, w: 3, h: 2 },
  { type: 'outbound_scheduled', x: 3, y: 0, w: 3, h: 2 },
  { type: 'outbound_voicemails', x: 6, y: 0, w: 3, h: 2 },
  { type: 'outbound_not_answered', x: 9, y: 0, w: 3, h: 2 },
  // Row 2 — Task metrics KPI cards. Width matches the scheduling tiles above
  // (w:3) so the two KPI rows read as one consistent grid; 3 metrics align
  // under the first three scheduling tiles.
  { type: 'open_tasks', x: 0, y: 2, w: 3, h: 2 },
  { type: 'completed_tasks', x: 3, y: 2, w: 3, h: 2 },
  { type: 'referrals_count', x: 6, y: 2, w: 3, h: 2 },
  // Row 4 — Scheduling timeline (outbound trend + disposition breakdown).
  { type: 'outbound_call_trend', x: 0, y: 4, w: 6, h: 4 },
  { type: 'outbound_outcomes', x: 6, y: 4, w: 6, h: 4 },
  // Row 8 — Throughput trend (flow) + Tasks by assignee side-by-side.
  { type: 'throughput_trend', x: 0, y: 8, w: 6, h: 4 },
  { type: 'tasks_by_assignee', x: 6, y: 8, w: 6, h: 4 },
  // Row 12 — SLA + label breakdown.
  { type: 'time_to_resolution_by_label', x: 0, y: 12, w: 6, h: 4 },
  { type: 'tasks_by_label', x: 6, y: 12, w: 6, h: 4 },
  // Row 16 — open task age, half-width.
  { type: 'open_tasks_by_age', x: 0, y: 16, w: 6, h: 4 },
]);

export function defaultDashboardLayout(): Array<{
  id: string;
  type: TileType;
  x: number;
  y: number;
  w: number;
  h: number;
}> {
  return DEFAULT_LAYOUT_POSITIONS.map((t) => ({ ...t, id: crypto.randomUUID() }));
}
